import { Box, Stack, styled } from '@mui/material';
import { useAppSelector } from '../../../hooks/useAppSelector';
import * as lookupSlice from '../../../data/lookup.slice';
import { useEffect } from 'react';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import SelectMenu from '../../forms/SelectMenu';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import TextInput from '../../forms/TextInput';
import { WorkflowActionType } from '../../../data/models/workflow.model';
import { WorkflowStepFormData } from '../../../data/workflow-form.model';
import { SendMessageRecipient } from '../../../data/models/simplified-workflow.model';
import simplifiedWorkflowUtils from '../../../services/simplifiedWorkflowUtils';

interface WorkflowAlertStepActionProps {
  action: WorkflowActionType.PublishAlert | WorkflowActionType.TaskHuman;
}

const PREFIX = 'WorkflowAlertStepAction';

const classes = {
  alertContainer: `${PREFIX}-alertContainer`,
  alertSelect: `${PREFIX}-alertSelect`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`& .${classes.alertContainer}`]: {
    gap: theme.spacing(2),
  },
  [`& .${classes.alertSelect}`]: {
    maxWidth: 300,
  },
}));

export default function WorkflowAlertStepAction({
  action,
}: WorkflowAlertStepActionProps) {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const alertEvents = useAppSelector(lookupSlice.selectAlertEvents);
  const alertTypes = useAppSelector(lookupSlice.selectAlertTypes);

  const { control, formState } = useFormContext<WorkflowStepFormData>();

  useEffect(() => {
    dispatch(lookupSlice.fetchLookupItemsAsync('alertEvent'));
    dispatch(lookupSlice.fetchLookupItemsAsync('alertType'));
  }, []);

  const alertEventValues = alertEvents.map((alertEvent) => ({
    key: alertEvent.alertEventName,
    value: alertEvent.alertEventBC,
  }));

  const alertTypeValues = alertTypes.map((alertType) => ({
    key: alertType.alertTypeName,
    value: alertType.alertTypeBC,
  }));

  const alertRecipientValues: Array<{ key: string; value: string }> =
    Object.values(SendMessageRecipient).map((value) => ({
      key: t(
        `workflows.${simplifiedWorkflowUtils.getTranslationKeyForSendMessageRecipient(value)}`,
      ),
      value,
    }));
  alertRecipientValues.unshift({
    key: t('workflows.selectOption'),
    value: '',
  });

  return (
    <StyledBox>
      <Stack direction="column" className={classes.alertContainer}>
        <SelectMenu
          name={`${action}.alertEventBC`}
          label={t('workflows.code')}
          className={classes.alertSelect}
          values={alertEventValues}
          valueAttr="value"
          labelAttr="key"
          control={control}
          required
          errorMessage={
            formState.errors?.[action]?.alertEventBC?.message as string
          }
          rules={{ required: t('common.requiredField') }}
        />
        {action === WorkflowActionType.PublishAlert && (
          <SelectMenu
            name={`${action}.alertRecipientBC`}
            label={t('workflows.recipient')}
            className={classes.alertSelect}
            values={alertRecipientValues}
            valueAttr="value"
            labelAttr="key"
            control={control}
          />
        )}
        <TextInput
          name={`${action}.alertDescription`}
          label={t('workflows.description')}
          control={control}
          errors={formState.errors}
          required
          rules={{ required: t('common.requiredField') }}
          multiline
        />
        <SelectMenu
          name={`${action}.alertTypeBC`}
          label={t('workflows.type')}
          className={classes.alertSelect}
          values={alertTypeValues}
          valueAttr="value"
          labelAttr="key"
          control={control}
          required
          errorMessage={
            formState.errors?.[action]?.alertTypeBC?.message as string
          }
          rules={{ required: t('common.requiredField') }}
        />
      </Stack>
    </StyledBox>
  );
}
